import React, { useState, useEffect } from 'react'
import { getUserId } from 'shared/modules/userManagement'
import {
  getCachedUnleashProxyData,
  hasCachedUnleashProxyData,
  setCachedUnleashProxyData,
  getCachedGrowthbookData,
  hasCachedGrowthbookData,
  setCachedGrowthbookData,
  getFreshFeatureFlagsData,
} from 'shared/modules/featureFlagsConfig'
import { FeatureFlagsRuntimeProvider } from 'shared/featureFlags/FeatureFlagsRuntimeProvider'

import useMe from 'shared/useMe'
import { CURRENT_USER as proUser } from 'pro/common/backendValues'
import { AppLoader } from './AppLoader'

const DEFAULT_GROWTHBOOK_CONFIG = { apiHost: '', clientKey: '' }

const isCacheOutdated = (cache, fetchedData) =>
  Object.keys(fetchedData).some(key => cache[key] !== fetchedData[key])

const hasCachedFeatureFlagsConfig = () =>
  hasCachedUnleashProxyData() && hasCachedGrowthbookData()

const FeatureFlagProvider = ({ children }) => {
  const me = useMe()
  const [unleashProxy, setUnleashProxy] = useState(getCachedUnleashProxyData)
  const [growthbook, setGrowthbook] = useState(getCachedGrowthbookData)
  const [isConfigLoaded, setIsConfigLoaded] = useState(
    hasCachedFeatureFlagsConfig
  )

  const fetchFeatureFlagConfig = async () => {
    try {
      const {
        unleash_proxy: unleashProxyFetchedData,
        growthbook: growthbookFetchedData,
      } = await getFreshFeatureFlagsData()

      const cachedUnleash = getCachedUnleashProxyData()
      if (
        !hasCachedUnleashProxyData() ||
        isCacheOutdated(cachedUnleash, unleashProxyFetchedData)
      ) {
        setUnleashProxy(unleashProxyFetchedData)
        setCachedUnleashProxyData(unleashProxyFetchedData)
      }

      const nextGrowthbook = growthbookFetchedData || DEFAULT_GROWTHBOOK_CONFIG
      const cachedGrowthbook = getCachedGrowthbookData()

      if (
        !hasCachedGrowthbookData() ||
        isCacheOutdated(cachedGrowthbook, nextGrowthbook)
      ) {
        setGrowthbook(nextGrowthbook)
        setCachedGrowthbookData(nextGrowthbook)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsConfigLoaded(true)
    }
  }

  useEffect(() => {
    fetchFeatureFlagConfig()
  }, [])

  const { url, clientKey } = unleashProxy

  if (!isConfigLoaded || !url || !clientKey) {
    return <AppLoader />
  }

  const userId = getUserId(me, proUser)

  return (
    <FeatureFlagsRuntimeProvider
      userId={userId}
      unleashProxy={unleashProxy}
      growthbook={growthbook}
    >
      {children}
    </FeatureFlagsRuntimeProvider>
  )
}

export default FeatureFlagProvider
